import { useMemo } from "react";
import queryString from "query-string";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useActivationCheck } from "@vega-api/generated/idp-v1/idp-v1";
import { type FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { ActionStatusMessage } from "@vega-core/components/Deprecated/ActionStatusMessage/ActionStatusMessage";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { NoticeType } from "@vega-core/components/Notice/Notice";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { ActivationSuccessScreen } from "./components/ActivationSuccessScreen";
import { LoginLayout } from "./components/LoginLayout";
import { getActivationFlowConfig } from "./activationFlowConfig";

export const ActivationNextStep = ({ portal }: { portal: FrontendPortal }) => {
  const { registrationCode = "" } = queryString.parse(location.search) as {
    registrationCode?: string;
  };

  const {
    global: { companyName },
  } = usePublicConfig();

  const {
    data: activationCheckData,
    isError,
    error,
    isSuccess,
    isLoading,
  } = useActivationCheck(
    {
      registrationCode,
    },
    {
      query: {
        enabled: isTruthy(registrationCode),
      },
    },
  );

  const statusMessage = useMemo(() => {
    if (isError) {
      const { description } = getErrorMessage(error, {
        defaultMessage: m.auth_oktaAccCreateFail(),
      });

      return description;
    }

    if (isSuccess) return m.auth_oktaAccCreateSuccess();

    return "";
  }, [error, isError, isSuccess]);

  const statusVariant = useMemo(() => {
    if (isSuccess) return NoticeType.SUCCESS;

    if (isError) return NoticeType.ERROR;

    return NoticeType.INFO;
  }, [isSuccess, isError]);

  const flowConfig = useMemo(
    () =>
      getActivationFlowConfig(activationCheckData?.userActivationType, {
        companyName,
        redirectUrl: activationCheckData?.redirectUrl,
      }),
    [
      activationCheckData?.userActivationType,
      activationCheckData?.redirectUrl,
      companyName,
    ],
  );

  if (isLoading) {
    return (
      <LoginLayout portal={portal}>
        <LoadingSpinner />
      </LoginLayout>
    );
  }

  return (
    <LoginLayout portal={portal}>
      <div className="flex flex-col gap-4 w-full">
        <ActivationSuccessScreen config={flowConfig} />
        <ActionStatusMessage message={statusMessage} type={statusVariant} />
      </div>
    </LoginLayout>
  );
};
